import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import Modal from '../components/Modal';
import InvoiceReport from '../components/InvoiceReport';

const DailyCustomerPurchases = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();
    const [customers, setCustomers] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isPrintOpen, setIsPrintOpen] = useState(false);
    const [isPayOpen, setIsPayOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editInvoice, setEditInvoice] = useState(null);
    const [payAmount, setPayAmount] = useState('');
    const [payNote, setPayNote] = useState('');

    useEffect(() => {
        // Fetch all customers
        authFetch('/api/customers').then(r => r.json()).then(d => setCustomers(Array.isArray(d) ? d : []));
        // Fetch today's invoices
        const today = new Date().toISOString().slice(0, 10);
        authFetch(`/api/invoices?date=${today}`).then(r => r.json()).then(d => setInvoices(Array.isArray(d) ? d : []));
    }, [authFetch]);

    // Group invoices by customer
    const customerPurchases = customers.map(c => {
        const custInvoices = invoices.filter(inv => inv.customer_id === c.id);
        const total = custInvoices.reduce((s, inv) => s + parseFloat(inv.total_amount || 0), 0);
        const paid = custInvoices.reduce((s, inv) => s + parseFloat(inv.amount_paid || 0), 0);
        return { ...c, invoices: custInvoices, total, paid };
    }).filter(c => c.invoices.length > 0);

    const handlePay = async (e) => {
        e.preventDefault();
        if (!selectedCustomer) return;
        const amount = parseFloat(payAmount);
        if (!amount || amount <= 0) {
            alert(t('Please enter a valid payment amount greater than 0.'));
            return;
        }
        const res = await authFetch(`/api/customers?action=pay&id=${selectedCustomer.id}`, {
            method: 'POST',
            body: JSON.stringify({ amount, note: payNote })
        });
        if (res.ok) {
            setIsPayOpen(false);
            setPayAmount('');
            setPayNote('');
        } else {
            alert(t('Payment failed'));
        }
    };

    // Edit invoice handlers
    const handleEditClick = (inv) => {
        setEditInvoice({ ...inv, items: inv.items ? [...inv.items] : [] });
        setIsEditOpen(true);
    };
    const handleEditItemChange = (i, field, val) => {
        setEditInvoice((ei) => {
            const items = [...ei.items];
            items[i] = { ...items[i], [field]: val };
            return { ...ei, items };
        });
    };
    const handleEditSave = async (e) => {
        e.preventDefault();
        if (!editInvoice) return;
        const res = await authFetch(`/api/invoices/${editInvoice.id}`, {
            method: 'PUT',
            body: JSON.stringify({ ...editInvoice, items: editInvoice.items })
        });
        if (res.ok) {
            setIsEditOpen(false);
            setEditInvoice(null);
        } else {
            alert(t('Failed to update invoice'));
        }
    };
    return (
        <div className="p-6 max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">{t('Daily Customer Purchases')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {customerPurchases.map(c => (
                    <div key={c.id} className="bg-white rounded shadow p-4">
                        <div className="flex justify-between items-center mb-2">
                            <div>
                                <div className="font-bold text-lg">{c.name}</div>
                                <div className="text-xs text-gray-500">{t('Phone')}: {c.phone}</div>
                            </div>
                            <button className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold" onClick={() => setSelectedCustomer(c)}>{t('View Purchases')}</button>
                        </div>
                        <div className="text-sm">{t('Total Today')}: <b>CFA{c.total.toFixed(2)}</b></div>
                        <div className="text-sm">{t('Paid')}: <span className="text-emerald-600 font-bold">CFA{c.paid.toFixed(2)}</span></div>
                        <div className="text-sm">{t('Credit')}: <span className="text-red-600 font-bold">CFA{(c.total - c.paid).toFixed(2)}</span></div>
                    </div>
                ))}
            </div>
            {/* Customer Purchases Modal */}
            <Modal isOpen={!!selectedCustomer} onClose={() => setSelectedCustomer(null)} title={selectedCustomer ? `${selectedCustomer.name} - ${t('Today Purchases')}` : ''}>
                {selectedCustomer && (
                    <div>
                        <div className="mb-2">
                            <b>{t('Total Today')}:</b> CFA{selectedCustomer.total.toFixed(2)}<br />
                            <b>{t('Paid')}:</b> <span className="text-emerald-600 font-bold">CFA{selectedCustomer.paid.toFixed(2)}</span><br />
                            <b>{t('Credit')}:</b> <span className="text-red-600 font-bold">CFA{(selectedCustomer.total - selectedCustomer.paid).toFixed(2)}</span>
                        </div>
                        <div className="mb-2">
                            <button className="bg-emerald-600 text-white px-3 py-1 rounded text-xs font-bold mr-2" onClick={() => setIsPayOpen(true)}>{t('Pay')}</button>
                            <button className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold" onClick={() => {
                                const c = selectedCustomer;
                                const invRows = c.invoices.map((inv, i) =>
                                    `<tr style="background:${i%2===0?'#f8fafc':'#fff'}">
                                        <td style="padding:5px 8px;font-family:monospace">#${inv.id}</td>
                                        <td style="padding:5px 8px;color:#9ca3af">${inv.created_at?new Date(inv.created_at).toLocaleTimeString():''}</td>
                                        <td style="padding:5px 8px;text-align:right;font-weight:700">CFA${parseFloat(inv.total_amount).toFixed(2)}</td>
                                        <td style="padding:5px 8px;text-align:right;color:#059669;font-weight:700">CFA${parseFloat(inv.amount_paid).toFixed(2)}</td>
                                        <td style="padding:5px 8px;text-align:right;color:#dc2626;font-weight:700">CFA${(parseFloat(inv.total_amount)-parseFloat(inv.amount_paid)).toFixed(2)}</td>
                                    </tr>`).join('');
                                const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Daily Purchases — ${c.name}</title>
                                    <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; padding: 20px; } @page { size: A4; margin: 10mm; }
                                    table { border-collapse: collapse; width: 100%; } th { padding: 8px; text-align: left; border-bottom: 2px solid #1f2937; font-size: 10px; text-transform: uppercase; color: #6b7280; background: #f9fafb; } td { border-bottom: 1px solid #f1f5f9; }</style>
                                </head><body>
                                    <div style="border-bottom:2px solid #1e293b;padding-bottom:10px;margin-bottom:14px">
                                        <div style="font-size:18px;font-weight:900;text-transform:uppercase">DAILY CUSTOMER PURCHASES</div>
                                        <div style="font-size:11px;color:#475569;margin-top:4px">Customer: <b>${c.name}</b> | Phone: <b>${c.phone||'N/A'}</b> | Date: <b>${new Date().toLocaleDateString()}</b></div>
                                        <div style="display:flex;gap:24px;margin-top:8px;font-size:12px">
                                            <span>Total: <b>CFA${c.total.toFixed(2)}</b></span>
                                            <span style="color:#059669">Paid: <b>CFA${c.paid.toFixed(2)}</b></span>
                                            <span style="color:#dc2626">Credit: <b>CFA${(c.total-c.paid).toFixed(2)}</b></span>
                                        </div>
                                    </div>
                                    <div className="w-full overflow-x-auto max-w-full">
<table><thead><tr><th>Invoice #</th><th>Time</th><th style="text-align:right">Total</th><th style="text-align:right">Paid</th><th style="text-align:right">Credit</th></tr></thead>
                                    <tbody>${invRows}</tbody></table>
</div>
                                </${'script'}>
                                </body></html>`.replace('</${"script"}>', '<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};' + '</script>');
                                const popup = window.open('', '_blank', 'width=900,height=600');
                                if (popup) { popup.document.write(html); popup.document.close(); }
                                else alert('Please allow popups for this site to print.');
                            }}>{t('Print All')}</button>
                        </div>
                        <div className="overflow-x-auto max-h-64">
                            <table className="min-w-full text-xs border">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="p-2 border">{t('Invoice #')}</th>
                                        <th className="p-2 border">{t('Time')}</th>
                                        <th className="p-2 border">{t('Total')}</th>
                                        <th className="p-2 border">{t('Paid')}</th>
                                        <th className="p-2 border">{t('Credit')}</th>
                                        <th className="p-2 border">{t('Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedCustomer.invoices.map(inv => (
                                        <tr key={inv.id}>
                                            <td className="p-2 border font-mono">{inv.id}</td>
                                            <td className="p-2 border">{inv.created_at ? new Date(inv.created_at).toLocaleTimeString() : ''}</td>
                                            <td className="p-2 border">CFA{parseFloat(inv.total_amount).toFixed(2)}</td>
                                            <td className="p-2 border text-emerald-600">CFA{parseFloat(inv.amount_paid).toFixed(2)}</td>
                                            <td className="p-2 border text-red-600">CFA{(parseFloat(inv.total_amount) - parseFloat(inv.amount_paid)).toFixed(2)}</td>
                                            <td className="p-2 border">
                                                <button className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold mr-1" onClick={() => { setSelectedInvoice(inv); setIsPrintOpen(true); }}>{t('Print')}</button>
                                                <button className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold" onClick={() => handleEditClick(inv)}>{t('Edit')}</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Modal>
            {/* Print Invoice Area */}
            {isPrintOpen && selectedInvoice && (
                <div className="hidden print:block print-container">
                    <InvoiceReport invoice={selectedInvoice} onClose={() => setIsPrintOpen(false)} />
                </div>
            )}
            {/* Edit Invoice Modal */}
            <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={t('Edit Invoice')}>
                {editInvoice && (
                    <form onSubmit={handleEditSave} className="space-y-4">
                        <div className="font-bold">{t('Invoice #')}{editInvoice.id}</div>
                        {editInvoice.items && editInvoice.items.map((item, i) => (
                            <div key={i} className="flex gap-2 mb-2 items-end">
                                <input type="text" className="flex-1 border p-2 rounded text-sm" value={item.product_name || ''} readOnly />
                                <input type="number" min="0.01" step="0.01" required placeholder={t('Qty')} className="w-20 border p-2 rounded text-sm" value={item.quantity} onChange={e => handleEditItemChange(i, 'quantity', e.target.value)} />
                                <input type="number" min="0" step="0.01" required placeholder={t('Price')} className="w-24 border p-2 rounded text-sm" value={item.unit_price} onChange={e => handleEditItemChange(i, 'unit_price', e.target.value)} />
                            </div>
                        ))}
                        <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded font-bold">{t('Save Changes')}</button>
                    </form>
                )}
            </Modal>
            {/* Payment Modal */}
            <Modal isOpen={isPayOpen} onClose={() => setIsPayOpen(false)} title={t('Record Payment')}>
                {selectedCustomer && (
                    <form onSubmit={handlePay} className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded border text-sm space-y-1">
                            <p>{t('Customer')}: <strong>{selectedCustomer.name}</strong></p>
                            <p>{t('Outstanding Balance')}: <strong className="text-red-600">CFA{(selectedCustomer.total - selectedCustomer.paid).toFixed(2)}</strong></p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t('Amount Received (CFA)')}</label>
                            <input required type="number" min="0.01" step="0.01" className="w-full border p-2 rounded mt-1" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t('Note (optional)')}</label>
                            <input className="w-full border p-2 rounded mt-1" value={payNote} onChange={e => setPayNote(e.target.value)} placeholder={t('Cash, bank transfer...')} />
                        </div>
                        <button type="submit" className="w-full bg-emerald-600 text-white py-2 rounded font-bold">{t('Record Payment')}</button>
                    </form>
                )}
            </Modal>
        </div>
    );
};

export default DailyCustomerPurchases;
